import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { Shell } from "@/components/Shell";
import { BottomSheet } from "@/components/BottomSheet";
import { money } from "@/lib/format";
import { Plus, Search, FolderOpen, FileText, ChevronRight } from "lucide-react";

const Input = ({ testid, ...p }) => (
  <input data-testid={testid} {...p} className="w-full bg-surfaceSecondary border border-border rounded-[12px] px-4 py-3 text-base text-onSurface focus:outline-none focus:border-brandPrimary transition-colors placeholder:text-muted-foreground" />
);

export const ClientForm = ({ initial = {}, onSubmit, submitting, cta = "Save client" }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "", notes: "", ...initial });
  return (
    <div className="flex flex-col gap-3">
      <Input testid="client-name" placeholder="Client name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
      <Input testid="client-email" placeholder="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      <Input testid="client-phone" placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
      <Input testid="client-address" placeholder="Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
      <textarea data-testid="client-notes" placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="w-full bg-surfaceSecondary border border-border rounded-[12px] px-4 py-3 text-base focus:outline-none focus:border-brandPrimary" />
      <button data-testid="client-submit" disabled={!f.name.trim() || submitting} onClick={() => onSubmit(f)} className="mt-1 bg-brandPrimary text-onBrand rounded-full h-12 font-heading font-bold hover:bg-brandSecondary transition-colors active:scale-[0.98] disabled:opacity-40">
        {submitting ? "Saving…" : cta}
      </button>
    </div>
  );
};

export default function JobFiles() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sheet, setSheet] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/clients").then(({ data }) => setClients(data.clients)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async (form) => {
    setSaving(true);
    try { const { data } = await api.post("/clients", form); toast.success("Job file created"); setSheet(false); load(); navigate(`/jobs/${data.client.id}`); }
    catch { toast.error("Could not create client"); } finally { setSaving(false); }
  };

  const filtered = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <Shell showNav title="Job Files" subtitle={`${clients.length} client${clients.length === 1 ? "" : "s"}`} right={
      <button data-testid="add-client-btn" onClick={() => setSheet(true)} className="w-10 h-10 rounded-full bg-brandPrimary text-onBrand flex items-center justify-center active:scale-90 transition-transform"><Plus size={22} /></button>
    }>
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients" className="w-full bg-surface border border-border rounded-full pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-brandPrimary" />
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-[68px] bg-surfaceTertiary rounded-[12px] animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-surface rounded-[16px] border border-dashed border-borderStrong p-10 text-center mt-6">
          <div className="w-12 h-12 rounded-full bg-brandTertiary flex items-center justify-center mx-auto mb-3"><FolderOpen size={22} className="text-brandSecondary" /></div>
          <p className="font-heading font-bold text-onSurface">No job files yet</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Create a folder for each client and keep every quote, invoice and note together.</p>
          <button data-testid="empty-add-client" onClick={() => setSheet(true)} className="bg-brandPrimary text-onBrand rounded-full px-5 h-11 font-heading font-bold text-sm active:scale-95 transition-transform">Add your first client</button>
        </div>
      ) : (
        <div className="space-y-2 pb-4 animate-fade-up">
          {filtered.map((c) => (
            <button key={c.id} data-testid={`client-${c.id}`} onClick={() => navigate(`/jobs/${c.id}`)} className="w-full bg-surface rounded-[12px] border border-border p-4 flex items-center gap-3 hover:border-brandPrimary transition-colors active:scale-[0.99] text-left">
              <div className="w-11 h-11 rounded-[12px] bg-brandTertiary flex items-center justify-center shrink-0">
                <span className="font-heading font-extrabold text-brandSecondary">{c.name.charAt(0).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm text-onSurface truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1"><FileText size={12} /> {c.document_count} document{c.document_count === 1 ? "" : "s"}</p>
              </div>
              <ChevronRight size={18} className="text-borderStrong" />
            </button>
          ))}
        </div>
      )}

      <BottomSheet open={sheet} onClose={() => setSheet(false)} title="New client" testid="add-client-sheet">
        <ClientForm onSubmit={add} submitting={saving} cta="Create job file" />
      </BottomSheet>
    </Shell>
  );
}
